import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Hands focus back to the control that opened a dialog when it closes. Radix
 * returns focus to a DialogTrigger, but a dialog opened from state has none,
 * so focus would fall to <body> and a keyboard user would start again from
 * the top of the page. Pass the result as DialogContent's onCloseAutoFocus.
 *
 *   const returnFocus = useReturnFocus(open);
 *   <DialogContent onCloseAutoFocus={returnFocus}>
 */
export function useReturnFocus(open: boolean): (event: Event) => void {
  const returnFocusRef = useRef<HTMLElement | null>(null);

  // A layout effect runs before the dialog's focus trap moves focus in.
  useLayoutEffect(() => {
    if (open) returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, [open]);

  return useCallback((event: Event) => {
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    // Gone (the row was removed) or never had focus: leave it to Radix.
    if (target && target.isConnected && target !== document.body) {
      event.preventDefault();
      target.focus();
    }
  }, []);
}
